import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Building, Truck, Calendar, Clock, Send, X, Edit2, Plus, Package } from "lucide-react";
import { FormulaBadges } from "@/components/FormulaBadges";
import { translateOptionName } from "@/lib/formatters";
import { fmtTime, fmtVolume } from "@/lib/utils";
import { motion } from "motion/react";
import type { OrderRecapData, OrderRecapTruck } from "@/components/OrderRecapCard";

interface PendingOrderRecapCardProps {
  recap: OrderRecapData;
  onConfirm: () => void;
  onCancel: () => void;
  onModifyVolume?: (workItemIndex: number) => void;
  onModifyWorkItem?: (workItemIndex: number) => void;
  onAddWorkItem?: () => void;
  onChangeFormula?: () => void;
  onAddTruckOptions?: (truckNumber: number) => void;
  isConfirming?: boolean;
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.05,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 12, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1] as const,
    },
  },
};

function TruckCard({ 
  truck, 
  onAddOptions 
}: { 
  truck: OrderRecapTruck; 
  onAddOptions?: () => void;
}) {
  const hasOptions = truck.options && truck.options.length > 0;
  
  return (
    <div className="p-md bg-muted/30 rounded-lg border border-border/50 space-y-sm">
      {/* Truck header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-sm">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
            <Truck className="h-4 w-4 text-primary" />
          </div>
          <div>
            <span className="text-label">Camion {truck.number}</span>
            <div className="flex items-center gap-1.5 text-helper text-muted-foreground">
              <span>{fmtVolume(truck.volume)} m³</span>
              <span>•</span>
              <Clock className="h-3 w-3" />
              <span>{truck.time}</span>
            </div>
          </div>
        </div>
        
        {truck.craneAccess && (
          <Badge variant="secondary" className="text-body-sm">
            🏗️ {truck.craneAccess}
          </Badge>
        )}
      </div>
      
      {/* Formula */}
      {truck.formula && truck.formula !== "Béton" && (
        <div className="text-helper text-muted-foreground pl-10">
          Formule: {truck.formula}
        </div>
      )}
      
      {/* Options */}
      {hasOptions && (
        <div className="pl-10 space-y-1">
          <span className="text-body-sm text-muted-foreground">Options:</span>
          <div className="flex flex-wrap gap-1">
            {truck.options.map((option, idx) => {
              const cost = option.price * (option.unit_type === 'euro_m3' ? truck.volume : 1);
              return (
                <Badge key={idx} variant="outline" className="text-body-sm gap-1">
                  {translateOptionName(option.name)}
                  <span className="text-muted-foreground">{cost.toFixed(0)}€</span>
                </Badge>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Add options button */}
      {onAddOptions && (
        <div className="pl-10">
          <Button
            variant="ghost"
            size="sm"
            onClick={onAddOptions}
            className="h-7 text-body-sm text-muted-foreground hover:text-primary"
          >
            <Plus className="h-3 w-3 mr-1" />
            {hasOptions ? "Modifier options" : "Ajouter options"}
          </Button>
        </div>
      )}
    </div>
  );
}

export function PendingOrderRecapCard({ 
  recap, 
  onConfirm, 
  onCancel,
  onModifyVolume,
  onModifyWorkItem,
  onAddWorkItem,
  onChangeFormula,
  onAddTruckOptions,
  isConfirming 
}: PendingOrderRecapCardProps) {
  const totalVolume = recap.workItems.reduce((sum, o) => sum + o.volume, 0);
  
  return (
    <motion.div 
      className="w-full max-w-[calc(100vw-2rem)] sm:max-w-lg space-y-sm sm:space-y-md"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header card */}
      <motion.div variants={itemVariants}>
        <Card className="border border-primary/20 bg-primary/5">
          <CardHeader className="px-md sm:px-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-sm">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                  <Package className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                </div>
                <div className="min-w-0">
                  <CardTitle className="text-body-md sm:text-subtitle">Commande en attente</CardTitle>
                  <p className="text-body-sm sm:text-body-md text-muted-foreground truncate">{recap.siteName}</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon" aria-label="Fermer"
                onClick={onCancel}
                className="h-7 w-7 sm:h-8 sm:w-8 text-muted-foreground hover:text-destructive shrink-0"
              >
                <X className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="px-md sm:px-lg space-y-sm sm:space-y-md">
            {/* Summary */}
            <div className="grid grid-cols-3 gap-1.5 sm:gap-md text-center">
              <div className="p-1.5 sm:p-2 bg-background rounded-lg border border-border/50">
                <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4 mx-auto mb-0.5 sm:mb-1 text-muted-foreground" />
                <p className="text-body-sm">{recap.date}</p>
              </div>
              <div className="p-1.5 sm:p-2 bg-background rounded-lg border border-border/50">
                <Package className="h-3.5 w-3.5 sm:h-4 sm:w-4 mx-auto mb-0.5 sm:mb-1 text-muted-foreground" />
                <p className="text-body-sm">{fmtVolume(totalVolume)} m³</p>
              </div>
              <div className="p-1.5 sm:p-2 bg-background rounded-lg border border-border/50">
                <Truck className="h-3.5 w-3.5 sm:h-4 sm:w-4 mx-auto mb-0.5 sm:mb-1 text-muted-foreground" />
                <p className="text-body-sm">{recap.trucks.length} camion{recap.trucks.length > 1 ? 's' : ''}</p>
              </div>
            </div>
            
            {/* Plant info */}
            {recap.plant && (
              <div className="flex items-center gap-sm text-body-md text-muted-foreground">
                <span>🏭</span>
                <span>Centrale {recap.plant}</span>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Ouvrages section */}
      <motion.div variants={itemVariants}>
        <Card>
          <CardHeader className="px-md sm:px-lg">
            <div className="flex items-center justify-between">
              <CardTitle className="text-body-sm sm:text-body-md">Ouvrages</CardTitle>
              {onAddWorkItem && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onAddWorkItem}
                  className="h-6 sm:h-7 text-body-sm"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Ajouter
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="px-md sm:px-lg">
            <div className="space-y-sm">
              {recap.workItems.map((workItem, idx) => (
                <div 
                  key={idx} 
                  className="p-2.5 bg-muted/30 rounded-lg border border-border/50"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-sm min-w-0 flex-1">
                      <Building className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <p className="text-body-md truncate">
                          {workItem.building && workItem.level 
                            ? `${workItem.building} - ${workItem.level}` 
                            : workItem.name}
                        </p>
                        {(workItem.building && workItem.level) && (
                          <p className="text-helper text-muted-foreground truncate">{workItem.name}</p>
                        )}
                        <div className="flex flex-wrap items-center gap-1 mt-1">
                          <Badge variant="outline" className="text-body-sm h-5 px-1.5">
                            {fmtVolume(workItem.volume)} m³
                          </Badge>
                          <Badge variant="time">{fmtTime(workItem.time)}</Badge>
                          {workItem.formula && (
                            <FormulaBadges
                              name={workItem.formula.name}
                              strength={workItem.formula.strength}
                              exposureClass={workItem.formula.exposure_class}
                              consistency={workItem.formula.consistency}
                              granulometry={workItem.formula.granulometry}
                              size="sm"
                            />
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Action buttons for each ouvrage */}
                    <div className="flex items-center gap-1 shrink-0">
                      {onModifyVolume && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onModifyVolume(idx)}
                          className="h-7 w-7 text-muted-foreground hover:text-primary"
                          title="Modifier le volume"
                          aria-label="Modifier le volume"
                        >
                          <Package className="h-3 w-3" />
                        </Button>
                      )}
                      {onModifyWorkItem && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onModifyWorkItem(idx)}
                          className="h-7 w-7 text-muted-foreground hover:text-primary"
                          title="Modifier l'ouvrage"
                          aria-label="Modifier l'ouvrage"
                        >
                          <Edit2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Trucks section */}
      <motion.div variants={itemVariants}>
        <Card>
          <CardHeader className="px-md sm:px-lg">
            <CardTitle className="text-body-sm sm:text-body-md">
              Camions ({recap.trucks.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="px-md sm:px-lg">
            <div className="space-y-1.5 sm:space-y-sm">
              {recap.trucks.map((truck) => (
                <TruckCard 
                  key={truck.number} 
                  truck={truck}
                  onAddOptions={onAddTruckOptions ? () => onAddTruckOptions(truck.number) : undefined}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Action buttons */}
      <motion.div 
        variants={itemVariants}
        className="flex gap-sm pt-1"
      >
        {onChangeFormula && (
          <Button 
            variant="outline" 
            onClick={onChangeFormula}
            className="flex-1 h-9 sm:h-11 text-body-sm sm:text-body-md"
          >
            <Edit2 className="mr-1.5 sm:mr-2 h-3.5 w-3.5 sm:h-4 sm:w-4" />
            Modifier
          </Button>
        )}
        <Button 
          onClick={onConfirm} 
          className="flex-1 h-9 sm:h-11 text-body-sm sm:text-body-md"
          disabled={isConfirming}
        >
          <Send className="mr-1.5 sm:mr-2 h-3.5 w-3.5 sm:h-4 sm:w-4" />
          {isConfirming ? "Envoi..." : "Envoyer à la centrale"}
        </Button>
      </motion.div>
    </motion.div>
  );
}
